import { useEffect, RefObject } from 'react'
import { throttle } from 'lodash'

const DEFAULT_HOVER_EXCLUDE_QUERY_SELECTOR = 'a[href], button, input, [data-hoist-above-parent-hover]'

/**
 * A utility to help with styling an element when it is hovered but not when particular descendants
 * are hovered.
 *
 * E.g. To change a list item bg to gray when it's hovered, but if you hover any links
 * buttons inside the list item, then it's background should not be gray. The technical challenge
 * is that the DOM thinks it is hovered, but from a user perspective they are hovering the button
 * or link that is nested inside the list item. So we want to show the user feedback that the outer
 * list item is NOT hovered.
 *
 * @param ref The outer ref element
 * @param onHoverChange Will be called, as the mouse moves, with true (if "hovered") or false (if not).
 * @param excludeQuerySelector Defaults to all link and button descendants of the ref element
 * @param [isEnabled]
 */
export function useHoverWithExcludedChildren({
  ref,
  onHoverChange = () => {},
  excludeQuerySelector = DEFAULT_HOVER_EXCLUDE_QUERY_SELECTOR,
  isEnabled = true
}: {
  ref: RefObject<HTMLElement | null>
  onHoverChange: (isHovered: boolean) => void
  excludeQuerySelector: string
  isEnabled?: boolean
}) {
  useEffect(() => {
    if (!ref.current || !isEnabled) return
    let isInside = false

    const onMouseEnter = () => {
      isInside = true
    }
    const onMouseLeave = () => {
      isInside = false
      onHoverChange(false)
    }
    const throttledHoverChange = throttle((target: HTMLElement) => {
      if (!ref.current || !target) return
      const excludedElements = Array.from(ref.current.querySelectorAll(excludeQuerySelector))
      if (isInside) {
        const isHovered =
          excludedElements?.length > 0
            ? !excludedElements.includes(target) && excludedElements.every(excluded => !excluded.contains(target))
            : true
        onHoverChange(isHovered)
        return
      }
      onHoverChange(false)
    }, 50)

    function onMouseMove(e: any) {
      throttledHoverChange(e.target)
    }

    ref.current.addEventListener('mouseenter', onMouseEnter)
    ref.current.addEventListener('mouseleave', onMouseLeave)
    ref.current.addEventListener('mousemove', onMouseMove)

    // eslint-disable-next-line consistent-return
    return () => {
      if (ref.current) {
        ref.current.removeEventListener('mouseenter', onMouseEnter)
        ref.current.removeEventListener('mouseleave', onMouseLeave)
        ref.current.removeEventListener('mousemove', onMouseMove)
      }
    }
  }, [isEnabled, onHoverChange, excludeQuerySelector])
}
